// Recovery from stale-chunk errors: a deploy replaced the hashed JS/CSS files
// this tab's index.html still points at. Shared by the error boundaries,
// lazyWithRetry and the global listeners in main.tsx.

export const RELOAD_FLAG = 'dentacares:chunk-reload';
const RELOAD_WINDOW_MS = 60_000;

type ReloadGuardState = { count: number; ts: number };

const readReloadGuard = (): ReloadGuardState => {
  try {
    const raw = sessionStorage.getItem(RELOAD_FLAG);
    if (!raw) return { count: 0, ts: 0 };
    const parsed = JSON.parse(raw) as ReloadGuardState;
    if (Date.now() - parsed.ts > RELOAD_WINDOW_MS) return { count: 0, ts: 0 };
    return parsed;
  } catch {
    return { count: 0, ts: 0 };
  }
};

const writeReloadGuard = (state: ReloadGuardState) => {
  try {
    sessionStorage.setItem(RELOAD_FLAG, JSON.stringify(state));
  } catch {
    // session storage unavailable — fall through
  }
};

export const isChunkLoadError = (err: unknown): boolean => {
  if (!err) return false;
  const message = err instanceof Error ? err.message : String(err);
  const name = err instanceof Error ? err.name : '';
  return (
    name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Loading chunk \d+ failed/i.test(message) ||
    // Vite's own preloader, when a page's CSS chunk is gone.
    /Unable to preload CSS/i.test(message)
    // NOTE: we deliberately do NOT match a bare TypeError ("Load failed" /
    // "Failed to fetch"). Those are also thrown by ordinary failed fetch()
    // calls (network blips, API errors) and treating them as stale-chunk
    // errors caused a reload loop on the "Something went wrong" screen.
    // A genuine stale dynamic import in any modern browser surfaces with one
    // of the explicit messages above.
  );
};

const clearRuntimeCaches = async () => {
  if (typeof window === 'undefined' || !('caches' in window)) return;
  try {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  } catch {
    // best-effort
  }
};

const updateServiceWorkers = async () => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator))
    return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.update().catch(() => undefined)));
  } catch {
    // best-effort
  }
};

// How many automatic reloads one stale-chunk episode may use. The first is a
// plain reload; the second adds a cache-busting query param in case the first
// was served a stale index.html (HTTP cache, a slow edge, the SW html-cache).
const MAX_AUTO_RELOADS = 2;
// Cache/SW housekeeping is best-effort. On a slow mobile connection
// `registration.update()` can hang for a long time, and the user would sit on
// whatever screen is up meanwhile — so the reload never waits longer than this.
const PRE_RELOAD_BUDGET_MS = 3_000;
const CACHE_BUST_PARAM = '__dc_reload';

// Set once THIS document has scheduled a reload. Several things notice the
// same missing chunk at once — the capture-phase asset `error` listener,
// `vite:preloadError`, lazyWithRetry, the boundaries — and before this flag
// the first caller consumed the guard and every later one was told "recovery
// exhausted", so the crash screen flashed (and sometimes stayed, while SW
// housekeeping dragged on) even though a reload was already on its way.
let reloadInFlight = false;

const withTimeout = (work: Promise<unknown>, ms: number) =>
  Promise.race([work, new Promise((resolve) => setTimeout(resolve, ms))]);

const navigateToLatest = (bustCache: boolean) => {
  if (!bustCache) {
    window.location.reload();
    return;
  }
  const url = new URL(window.location.href);
  url.searchParams.set(CACHE_BUST_PARAM, Date.now().toString(36));
  window.location.replace(url.toString());
};

// Attempts recovery for stale-chunk errors. Returns true if a reload is under
// way (now or already); the caller should show the neutral "updating" screen
// and let the page navigate. Returns false only once the automatic reloads are
// used up — a genuine loop, or the network is down — and the caller should then
// show ChunkRecoveryScreen's `failed` state rather than a crash card.
export const tryRecoverFromChunkError = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (reloadInFlight) return true;
  const guard = readReloadGuard();
  if (guard.count >= MAX_AUTO_RELOADS) return false;
  // Persist the incremented count synchronously *before* kicking off the async
  // reload, so a reload loop across documents is still bounded.
  writeReloadGuard({ count: guard.count + 1, ts: Date.now() });
  reloadInFlight = true;
  const bustCache = guard.count > 0;
  void (async () => {
    await withTimeout(
      Promise.all([clearRuntimeCaches(), updateServiceWorkers()]),
      PRE_RELOAD_BUDGET_MS,
    );
    navigateToLatest(bustCache);
  })();
  return true;
};

// Manual escape hatch for the recovery screen: start a fresh episode and load
// the newest build, bypassing any cached HTML.
export const reloadToLatest = (): void => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // ignore
  }
  reloadInFlight = true;
  navigateToLatest(true);
};

// Removes the cache-busting param a recovery reload added, before the router
// reads the URL, so it never leaks into links, analytics or bookmarks.
export const stripCacheBustParam = (): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has(CACHE_BUST_PARAM)) return;
  url.searchParams.delete(CACHE_BUST_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());
};

// Called once the app has successfully rendered. Clears the one-shot reload
// guard so a single, unrelated chunk error later on can still self-heal — while
// a true reload *loop* (error → reload → same error before we ever mount) never
// reaches this and stays blocked by the guard.
export const clearReloadGuard = (): void => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // session storage unavailable — nothing to clear
  }
};
